import React, { useRef, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { ArrowUp, Mic, Loader } from 'lucide-react';
import { Square, Stop } from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { DictationMode } from '../../../dictation/dictationSlice';
import { getCurrentOutputLanguage } from '../../../../hooks/useOutputLanguage';
import DictationService from '../../../../services/dictationService';
import { RootState } from '../../../../reducers';
import { FileType } from '../../../../types';
import MentionSelector from './MentionSelector';
import { useBatchRecorder } from '../../../../hooks/useBatchRecorder';

export interface ChatInputChip {
  type?: 'file' | 'video' | 'ocr' | 'custom';
  label?: string;
  icon?: React.ReactNode;
  onRemove?: () => void;
  onClick?: () => void;
  element?: React.ReactNode; // For fully custom chips (when provided, type and label are optional)
}

export interface ChatInputProps {
  // Input value and handlers
  value: string;
  onChange: (value: string) => void;
  onSubmit: (message?: string) => void;
  placeholder: string;
  disabled?: boolean;
  disableSend?: boolean; // Additional condition to disable send button

  // Loading state
  isLoading?: boolean;
  loadingIcon?: 'square' | 'spinner';
  onStopLoading?: () => void;

  // Feature chips
  chips?: ChatInputChip[];

  // Left button configuration
  leftButton?: {
    icon: React.ReactNode;
    onClick: () => void;
    ariaLabel: string;
  };

  // Focus state
  isFocused?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;

  // Behavior
  maxHeight?: number;
  multilineThreshold?: number;
  onKeyPress?: (event: React.KeyboardEvent) => void;

  // Styling
  containerClassName?: string;
  inactiveOpacity?: boolean; // Reduce opacity when inactive

  // Extra content (dropdowns, menus, etc.)
  children?: React.ReactNode;

  // Input ref (optional external ref for the textarea)
  inputRef?: React.RefObject<HTMLTextAreaElement>;

  // Dictation support
  enableDictation?: boolean; // Show dictation button when input is empty
  dictationMode?: DictationMode;
  autoSendOnDictation?: boolean; // Auto-submit after dictation completes

  // @ mention support
  enableMention?: boolean; // Enable @ to trigger file selector
  selectedFiles?: string[]; // Currently selected file IDs for mention selector
  onMentionSelect?: (file: FileType) => void; // Called when a file is selected via @
}

export const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSubmit,
  placeholder,
  disabled = false,
  disableSend = false,
  isLoading = false,
  loadingIcon = 'square',
  onStopLoading,
  chips = [],
  leftButton,
  isFocused: externalFocused,
  onFocus,
  onBlur,
  maxHeight = 150,
  multilineThreshold = 40,
  onKeyPress,
  containerClassName = '',
  inactiveOpacity = false,
  children,
  inputRef: externalInputRef,
  enableDictation = false,
  dictationMode = 'fast',
  autoSendOnDictation = true,
  enableMention = false,
  selectedFiles = [],
  onMentionSelect,
}) => {
  const internalTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [internalFocused, setInternalFocused] = useState(false);
  const [isMultiline, setIsMultiline] = useState(false);

  const [isDictationProcessing, setIsDictationProcessing] = useState(false);

  // Get team custom vocabulary from Redux (read-only)
  const teamCustomVocabulary =
    useSelector(
      (state: RootState) =>
        state.team.selectedTeam?.fileOptions?.customVocabulary,
    ) || '';

  const batchRecorder = useBatchRecorder({
    onAudioReady: useCallback(
      async (audioBlob: Blob) => {
        setIsDictationProcessing(true);
        try {
          const currentOutputLanguage = await getCurrentOutputLanguage();
          const transcript = await DictationService.processBatchAudioDictation(
            audioBlob,
            {
              mode: dictationMode,
              customVocabulary: teamCustomVocabulary,
              outputLanguage: currentOutputLanguage,
            },
          );
          if (transcript && transcript.trim()) {
            onChange(transcript);
            if (autoSendOnDictation) {
              onSubmit(transcript);
            }
          }
        } catch (err) {
          console.error('Failed to process dictation:', err);
        } finally {
          setIsDictationProcessing(false);
        }
      },
      [
        dictationMode,
        teamCustomVocabulary,
        autoSendOnDictation,
        onChange,
        onSubmit,
      ],
    ),
  });

  // @ mention state
  const [mentionState, setMentionState] = useState({
    isOpen: false,
    query: '',
    startIndex: -1,
  });
  const lastCursorPositionRef = useRef<number>(0);

  // Use external ref if provided, otherwise use internal ref
  const textareaRef = externalInputRef || internalTextareaRef;

  const isFocused = externalFocused ?? internalFocused;
  const hasChips = chips.length > 0;

  // Reset textarea height when value is cleared externally (e.g., after submit)
  React.useEffect(() => {
    if (!value && textareaRef.current) {
      // Add transition for smooth height animation
      textareaRef.current.style.transition = 'height 150ms ease-out';
      textareaRef.current.style.height = 'auto';
      setIsMultiline(false);
      // Remove transition after animation completes to not interfere with typing
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.style.transition = '';
        }
      }, 150);
    }
  }, [value, textareaRef]);

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursorPosition = e.target.selectionStart || 0;
    lastCursorPositionRef.current = cursorPosition;

    // @ mention detection
    if (enableMention) {
      const charBeforeCursor = newValue[cursorPosition - 1];
      const charBeforeAt = newValue[cursorPosition - 2];

      // Detect @ trigger: @ at start of input, or @ preceded by whitespace
      if (
        charBeforeCursor === '@' &&
        (cursorPosition === 1 || !charBeforeAt || /\s/.test(charBeforeAt))
      ) {
        setMentionState({
          isOpen: true,
          query: '',
          startIndex: cursorPosition - 1,
        });
      } else if (mentionState.isOpen && mentionState.startIndex >= 0) {
        // Update query while mention is open
        const queryText = newValue.slice(
          mentionState.startIndex + 1,
          cursorPosition,
        );

        // Close if cursor moved before @ or @ was deleted
        if (
          cursorPosition <= mentionState.startIndex ||
          newValue[mentionState.startIndex] !== '@'
        ) {
          setMentionState({ isOpen: false, query: '', startIndex: -1 });
        } else if (queryText.includes(' ')) {
          // Close if space is typed (end of mention)
          setMentionState({ isOpen: false, query: '', startIndex: -1 });
        } else {
          setMentionState((prev) => ({ ...prev, query: queryText }));
        }
      }
    }

    onChange(newValue);
    // Auto-resize
    e.target.style.height = 'auto';
    const newHeight = Math.min(e.target.scrollHeight, maxHeight);
    e.target.style.height = `${newHeight}px`;
    setIsMultiline(newHeight > multilineThreshold);
  };

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setInternalFocused(true);
    onFocus?.();
    // Ensure proper height on focus
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, maxHeight)}px`;
  };

  const handleBlur = () => {
    setInternalFocused(false);
    onBlur?.();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value.trim() && !disabled && !isLoading && !disableSend) {
      onSubmit();
      // Reset textarea height after submit
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        setIsMultiline(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // If mention selector is open, let it handle keyboard events
    if (mentionState.isOpen) {
      if (
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown' ||
        e.key === 'Enter' ||
        e.key === 'Tab'
      ) {
        // Let the MentionSelector handle these via its own keyboard listener
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionState({ isOpen: false, query: '', startIndex: -1 });
        return;
      }
    }

    // Enter without shift = submit
    // Shift+Enter = new line
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !disabled && !isLoading && !disableSend) {
        onSubmit();
        // Reset textarea height after submit
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
          setIsMultiline(false);
        }
      }
    }
  };

  // Handle mention file selection
  const handleMentionSelect = useCallback(
    (file: FileType) => {
      if (!onMentionSelect) return;

      // Remove the @query from input
      if (mentionState.startIndex >= 0) {
        const cursorPos = lastCursorPositionRef.current;
        const beforeMention = value.slice(0, mentionState.startIndex);
        const afterMention = value.slice(cursorPos);
        onChange(beforeMention + afterMention);
      }

      // Close mention selector
      setMentionState({ isOpen: false, query: '', startIndex: -1 });

      // Call the callback to add the file
      onMentionSelect(file);

      // Focus back on input
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
    },
    [onMentionSelect, mentionState.startIndex, value, onChange, textareaRef],
  );

  // Close mention when clicking outside
  const handleMentionClose = useCallback(() => {
    setMentionState({ isOpen: false, query: '', startIndex: -1 });
  }, []);

  return (
    <div className={cn('px-3 pb-3', containerClassName)}>
      <div className="max-w-3xl mx-auto w-full">
        <form onSubmit={handleSubmit} className="w-full">
          <div
            className={cn(
              'bg-white dark:bg-background border shadow-sm',
              'transition-all duration-300 ease-in-out',
              'rounded-[20px]',
              isFocused
                ? 'border-primary shadow-md'
                : 'border-border shadow-sm',
              // Adjust layout when chips are present
              hasChips
                ? 'p-1.5 flex flex-col gap-1.5'
                : 'px-1.5 py-1 flex items-end gap-1',
            )}
          >
            {/* Textarea - shows above when chips are present */}
            {hasChips && (
              <div className={cn('w-full px-1 py-1.5')}>
                <Textarea
                  ref={textareaRef}
                  className={cn(
                    'w-full min-h-[24px] max-h-[150px] resize-none border-0 px-2 py-1',
                    'text-sm leading-6',
                    'focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent',
                    'overflow-y-auto scrollbar-thin',
                    inactiveOpacity
                      ? 'placeholder:text-muted-foreground/50'
                      : 'placeholder:text-muted-foreground',
                  )}
                  placeholder={placeholder}
                  aria-label="chat input"
                  value={value}
                  onChange={handleTextareaChange}
                  onKeyPress={onKeyPress}
                  onKeyDown={handleKeyDown}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  disabled={disabled}
                  rows={1}
                />
              </div>
            )}

            {/* Main row with left button, input/chips, and send button */}
            <div
              className={cn(
                'flex gap-1 w-full',
                isMultiline ? 'items-end pb-0.5' : 'items-center',
              )}
            >
              {/* Left button (Settings/Plus) */}
              {leftButton && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={leftButton.onClick}
                  className="h-7 w-7 rounded-full hover:bg-accent flex-shrink-0"
                  aria-label={leftButton.ariaLabel}
                >
                  {leftButton.icon}
                </Button>
              )}

              {/* Middle - Either textarea or chips */}
              {!hasChips && (
                <Textarea
                  ref={textareaRef}
                  className={cn(
                    'flex-1 min-h-[24px] max-h-[150px] resize-none border-0 pl-1 pr-2 py-1',
                    'text-sm leading-6',
                    'focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent',
                    'overflow-y-auto scrollbar-thin',
                    inactiveOpacity
                      ? 'placeholder:text-muted-foreground/50'
                      : 'placeholder:text-muted-foreground',
                  )}
                  placeholder={placeholder}
                  aria-label="chat input"
                  value={value}
                  onChange={handleTextareaChange}
                  onKeyPress={onKeyPress}
                  onKeyDown={handleKeyDown}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  disabled={disabled}
                  rows={1}
                />
              )}

              {/* Chips - shows when present */}
              {hasChips && (
                <div className="flex-1 flex items-center gap-2 flex-wrap">
                  {chips.map((chip, index) => {
                    // If custom element provided, use it
                    if (chip.element) {
                      return (
                        <React.Fragment key={index}>
                          {chip.element}
                        </React.Fragment>
                      );
                    }

                    // Otherwise render default chip
                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={chip.onClick}
                        className="group flex items-center gap-2 h-7 bg-primary/10 hover:bg-primary/15 text-primary rounded-full px-3 transition-all animate-in slide-in-from-left-2 fade-in duration-300"
                      >
                        {chip.icon && (
                          <div className="w-4 h-4">{chip.icon}</div>
                        )}
                        <span className="text-xs font-medium">
                          {chip.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Right side - Send/Stop/Dictation button */}
              <TooltipProvider>
                <div
                  className={cn(
                    'flex gap-1 flex-shrink-0',
                    isMultiline ? 'items-end' : 'items-center',
                  )}
                >
                  {(() => {
                    // Loading state - show stop button
                    if (isLoading) {
                      return (
                        <Button
                          type="button"
                          onClick={onStopLoading}
                          size="icon"
                          variant="default"
                          className="h-7 w-7 rounded-full transition-all duration-300"
                        >
                          {loadingIcon === 'square' ? (
                            <Square
                              className="w-3.5 h-3.5 text-white"
                              weight="fill"
                            />
                          ) : (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          )}
                        </Button>
                      );
                    }

                    // Show dictation button when input is empty (like ChatGPT)
                    if (
                      enableDictation &&
                      !value.trim() &&
                      !batchRecorder.isRecording &&
                      !isDictationProcessing
                    ) {
                      return (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              onClick={batchRecorder.start}
                              size="icon"
                              variant="ghost"
                              disabled={disabled}
                              className="h-7 w-7 rounded-full transition-all duration-300 hover:bg-muted/50"
                            >
                              <Mic className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Dictate your message</TooltipContent>
                        </Tooltip>
                      );
                    }

                    // Show stop button while recording
                    if (batchRecorder.isRecording) {
                      return (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              onClick={batchRecorder.stop}
                              size="icon"
                              variant="destructive"
                              className="h-7 w-7 rounded-full transition-all duration-300"
                            >
                              <Stop
                                className="h-4 w-4 text-white"
                                weight="fill"
                              />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Stop recording</TooltipContent>
                        </Tooltip>
                      );
                    }

                    // Show processing spinner during dictation
                    if (isDictationProcessing) {
                      return (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          disabled
                          className="h-7 w-7 rounded-full transition-all duration-300"
                        >
                          <Loader className="h-4 w-4 animate-spin text-muted-foreground" />
                        </Button>
                      );
                    }

                    // Default: Show send button
                    return (
                      <Button
                        type="submit"
                        size="icon"
                        disabled={!value.trim() || disabled || disableSend}
                        variant={
                          !value.trim() || disabled || disableSend
                            ? 'ghost'
                            : 'default'
                        }
                        className={cn(
                          'h-7 w-7 rounded-full transition-all duration-300',
                          (!value.trim() || disabled || disableSend) &&
                            'bg-muted hover:bg-muted/80',
                          value.trim() &&
                            !disabled &&
                            !disableSend &&
                            'bg-primary hover:bg-primary/90 hover:scale-105',
                        )}
                      >
                        <ArrowUp
                          className={cn(
                            'h-4 w-4 transition-all duration-300',
                            !value.trim() || disabled || disableSend
                              ? 'text-muted-foreground'
                              : 'text-primary-foreground',
                            value.trim() &&
                              !disabled &&
                              !disableSend &&
                              'hover:-translate-y-0.5',
                          )}
                        />
                      </Button>
                    );
                  })()}
                </div>
              </TooltipProvider>
            </div>
          </div>
        </form>

        {/* @ Mention Selector */}
        {enableMention && mentionState.isOpen && (
          <div className="relative">
            <MentionSelector
              isOpen={mentionState.isOpen}
              query={mentionState.query}
              onSelect={handleMentionSelect}
              onClose={handleMentionClose}
              selectedFiles={selectedFiles}
            />
          </div>
        )}

        {/* Extra content (dropdowns, menus, etc.) */}
        {children}
      </div>
    </div>
  );
};
